import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Star, Users, Heart, TrendingUp } from 'lucide-react';
import '../../../assets/styles/customer/layout/StatsSection.css';

const StatsSection = () => {
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  const stats = [
    {
      id: 1,
      icon: <Star size={40} strokeWidth={2} />,
      value: 4.9,
      suffix: '/5',
      label: 'Average Rating',
      decimals: 1,
      color: '#ffc107'
    },
    {
      id: 2,
      icon: <Users size={40} strokeWidth={2} />,
      value: 500,
      suffix: '+',
      label: 'Active Distributors',
      decimals: 0,
      color: '#3e96e7'
    },
    {
      id: 3,
      icon: <Heart size={40} strokeWidth={2} />,
      value: 25000,
      suffix: '+',
      label: 'Happy Customers',
      decimals: 0,
      color: '#f44336'
    },
    {
      id: 4,
      icon: <TrendingUp size={40} strokeWidth={2} />,
      value: 1000000,
      suffix: '+',
      label: 'Deliveries Tracked',
      decimals: 0,
      color: '#4caf50',
      format: 'M'
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
          }
        });
      },
      {
        threshold: 0.3 // Trigger when 30% of section is visible
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasAnimated]);

  // Counter Animation Component
  const CounterStat = ({ stat, shouldAnimate }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
      if (!shouldAnimate) return;

      const duration = 2500; // 2.5 seconds
      const steps = 60;
      const increment = stat.value / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        if (currentStep <= steps) {
          setCount(increment * currentStep);
        } else {
          setCount(stat.value);
          clearInterval(timer);
        }
      }, duration / steps);

      return () => clearInterval(timer);
    }, [shouldAnimate, stat.value]);

    const formatNumber = (num) => {
      if (stat.format === 'M' && num >= 1000000) {
        return (num / 1000000).toFixed(stat.decimals) + 'M';
      } else if (num >= 1000) {
        return Math.floor(num).toLocaleString();
      }
      return num.toFixed(stat.decimals);
    };

    return (
      <Col lg={3} md={6} sm={6} xs={12} key={stat.id}>
        <div 
          className="stat-card"
          style={{ 
            animationDelay: `${stat.id * 0.15}s`,
            '--stat-color': stat.color
          }}
        >
          {/* Icon */}
          <div 
            className="stat-icon"
            style={{ color: stat.color }}
          >
            {stat.icon}
          </div>

          {/* Counter */}
          <div className="stat-number">
            <span className="stat-value">
              {formatNumber(count)}
            </span>
            <span className="stat-suffix">{stat.suffix}</span>
          </div>

          {/* Label */}
          <p className="stat-label">{stat.label}</p>

          {/* Progress Bar */}
          <div className="stat-progress">
            <div 
              className="stat-progress-bar"
              style={{ 
                backgroundColor: stat.color,
                width: shouldAnimate ? '100%' : '0%'
              }}
            ></div>
          </div>
        </div>
      </Col>
    );
  };

  return (
    <section className="stats-section" ref={sectionRef}>
      
      {/* Animated Background */}
      <div className="stats-bg-decoration">
        <div className="stats-wave">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path 
              fill="rgba(62, 150, 231, 0.08)" 
              d="M0,32L48,37.3C96,43,192,53,288,58.7C384,64,480,64,576,58.7C672,53,768,43,864,48C960,53,1056,75,1152,74.7C1248,75,1344,53,1392,42.7L1440,32L1440,120L1392,120C1344,120,1248,120,1152,120C1056,120,960,120,864,120C768,120,672,120,576,120C480,120,384,120,288,120C192,120,96,120,48,120L0,120Z"
            ></path>
          </svg>
        </div>
        <div className="stats-circle stats-circle-1"></div>
        <div className="stats-circle stats-circle-2"></div>
      </div>

      <Container>
        <Row className="g-4 stats-row">
          {stats.map((stat) => (
            <CounterStat 
              key={stat.id} 
              stat={stat} 
              shouldAnimate={hasAnimated} 
            />
          ))}
        </Row>
      </Container>

    </section>
  );
};

export default StatsSection;